import {Placement} from '@popperjs/core';
import React, {FC, RefObject, useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {useEventHandler} from '../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ConversationInquiryModel} from '../../../../libs/shared/core/src/models/api/conversationInquiriesModel';
import {isConversationSummaryFact} from '../../../../libs/shared/core/src/models/api/factModel';
import {
  LoadedMessageModel,
  MessageVisualStatusesEnum,
} from '../../../../libs/shared/core/src/models/api/messageModel';
import {ResolveStateProps} from '../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {NewPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../srcCommon/helpers/reselectHelpers';
import {ConversationModel} from '../../../models/api/conversationModel';
import {AppState} from '../../../store/appState';
import {getHasAISummarizeFeature} from '../../../store/data/company/companySelectors';
import {makeGetVisualRecipientMap} from '../../../store/data/contacts/recipientsSelectors';
import {getMessages} from '../../../store/data/dataSelectors';
import {fetchMessageAsyncAction} from '../../../store/data/messages/messagesActions';
import {ConversationPreview} from './conversationPreview';

/*
 * Props.
 */

export interface ConversationPreviewContainerProps {
  panelId: NewPanelId;
  conversation: ConversationModel;
  similarInquiry?: ConversationInquiryModel;
  anchor: RefObject<HTMLElement>;
  viewerConversationId: number | undefined;
  shouldRenderSummaryIfAvailable?: boolean;
  placement?: Placement;
  openConversation?: (model: ConversationModel) => void;
}

type ConversationPreviewContainerComponentProps = ConversationPreviewContainerProps &
  ResolveStateProps<typeof mapStateToProps> &
  ResolveDispatchProps<typeof mapDispatchToProps>;

/*
 * Selectors.
 */

function getConversationNamespace(_state: AppState, props: ConversationPreviewContainerProps) {
  return props.conversation.namespace;
}

function getConversationSummary(_state: AppState, props: ConversationPreviewContainerProps) {
  return props.conversation.facts.find(isConversationSummaryFact);
}

function getLastMessageUid(_state: AppState, props: ConversationPreviewContainerProps) {
  return props.conversation.lastMessage?.uid;
}

function makeGetLastMessage() {
  return createSelector(
    [getLastMessageUid, getMessages],
    (messageUid, messages): LoadedMessageModel | undefined => {
      if (!messageUid) {
        return undefined;
      }

      const message = messages[messageUid];
      if (message?.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
        return undefined;
      }

      return message;
    },
  );
}

/*
 * Component.
 */

const ConversationPreviewContainerComponent: FC<ConversationPreviewContainerComponentProps> = (props) => {
  const {
    panelId,
    conversation,
    anchor,
    viewerConversationId,
    messageUid,
    message,
    recipients,
    shouldRenderSummaryIfAvailable,
    similarInquiry,
    summary,
    fetchMessageAsync,
    hasAiSummarizeFeature,
    placement,
    openConversation,
  } = props;
  const [asyncStatus, setAsyncStatus] = useState(
    message ? AsyncStatusesEnum.LOADED : AsyncStatusesEnum.LOADING,
  );

  const onMessageRequest = useEventHandler(async () => {
    if (!messageUid || message?.uid === messageUid) {
      setAsyncStatus(AsyncStatusesEnum.LOADED);
      return;
    }

    setAsyncStatus(AsyncStatusesEnum.LOADING);
    try {
      await fetchMessageAsync(messageUid);
      setAsyncStatus(AsyncStatusesEnum.LOADED);
    } catch (error) {
      setAsyncStatus(AsyncStatusesEnum.FAILED);
      throw error;
    }
  });

  useEffect(() => {
    onMessageRequest();
  }, [messageUid, onMessageRequest]);

  return (
    <ConversationPreview
      panelId={panelId}
      conversationId={conversation.id}
      anchor={anchor}
      message={message}
      conversation={conversation}
      similarInquiry={similarInquiry}
      recipients={recipients}
      viewerConversationId={viewerConversationId}
      asyncStatus={asyncStatus}
      shouldRenderSummaryIfAvailable={shouldRenderSummaryIfAvailable}
      summary={summary}
      onRetryClick={onMessageRequest}
      hasAiSummarizeFeature={hasAiSummarizeFeature}
      placement={placement}
      openConversation={openConversation}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationPreviewContainerProps>()(() => {
  const getLastMessage = makeGetLastMessage();

  return {
    summary: getConversationSummary,
    messageUid: getLastMessageUid,
    message: getLastMessage,
    recipients: makeGetVisualRecipientMap(getConversationNamespace, getLastMessage),
    hasAiSummarizeFeature: getHasAISummarizeFeature,
  };
});

const mapDispatchToProps = {
  fetchMessageAsync: fetchMessageAsyncAction,
};

export const ConversationPreviewContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ConversationPreviewContainerComponent);
